import { Redirect } from '@omnea-app/fe-routing/Redirect'
import { FullWidthLoadingFallback } from '@omnea-app/frontend/components/shared/Loading/components/LoadingFallbacks'
import { CustomErrorBoundary } from '@omnea-app/frontend/utils/error-boundary/CustomErrorBoundary'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { useAuthzContext } from './AuthzContextProvider'
import { resolveRouteKey } from './resolveRouteKey'
import { ROUTE_REGISTRY } from './routeRegistry'
import { useRouteAccess } from './useRouteAccess'
import type { ParsedUrlQuery } from 'querystring'
import type { PropsWithChildren } from 'react'

const DEFAULT_DENIED_REDIRECT = '/'

export type GatedRoute = { pattern: string; query: ParsedUrlQuery; isQueryResolved: boolean }

type RouteAccessGateProps = PropsWithChildren<{ route: GatedRoute }>

/**
 * Withholds a route the caller may not reach. Mounted only for authenticated callers, since
 * `useRouteAccess` reads a context the unauthenticated tree never mounts.
 */
export const RouteAccessGate = ({ children, route }: RouteAccessGateProps) => {
  const access = useRouteAccess()
  const authzContext = useAuthzContext()

  // Next leaves the query empty until hydration, and a key discriminated by a query segment would
  // resolve to nothing — rendering the route ungated rather than withheld.
  if (route.isQueryResolved === false) return <FullWidthLoadingFallback />

  const routeKey = resolveRouteKey(route.pattern, route.query)

  // A route the registry does not name stays reachable, so coverage can grow without a missing key
  // becoming a silent outage.
  if (routeKey == null) return <>{children}</>

  const definition = ROUTE_REGISTRY[routeKey]
  // Every `RouteKey` is answered, so a miss is impossible; withhold rather than deny if one ever is.
  const answer = access.get(routeKey) ?? 'pending'

  switch (answer) {
    case 'unavailable':
      return <CustomErrorBoundary errorData={{ error: authzContext.status === 'unavailable' ? authzContext.error : undefined }} />
    case 'pending':
      return <FullWidthLoadingFallback />
    case 'allowed':
      return <>{children}</>
    case 'denied': {
      const deniedRedirectTo = definition.exempt === true ? undefined : definition.deniedRedirectTo
      return <Redirect to={deniedRedirectTo ?? DEFAULT_DENIED_REDIRECT} type="replace" />
    }
    default:
      return assertNever(answer)
  }
}
